import { z } from "zod";

export const phoneNumberSchema = z
  .string()
  .min(5, "phoneNumber must be at least 5 characters");
export const purposeSchema = z.string().min(2, "purpose is required");
export const verificationCodeSchema = z
  .string()
  .regex(/^[0-9]{4,8}$/, "code must be 4–8 digits");

const APPOINTMENT_PURPOSE_PREFIXES = ["cancel:", "reschedule:"] as const;

export const sendVerificationRequestSchema = z
  .object({
    phoneNumber: phoneNumberSchema.optional(),
    email: z.string().email().optional(),
    purpose: purposeSchema,
    appointmentId: z.string().min(1).optional(),
  })
  .superRefine((data, ctx) => {
    const isAppointmentPurpose = APPOINTMENT_PURPOSE_PREFIXES.some((prefix) =>
      data.purpose.startsWith(prefix),
    );
    if (!data.phoneNumber && !data.email && !isAppointmentPurpose) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phoneNumber"],
        message:
          "phoneNumber is required unless sending a cancel/reschedule verification",
      });
    }
    if (data.appointmentId && !isAppointmentPurpose) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["appointmentId"],
        message:
          "appointmentId can only be provided for cancel/reschedule verifications",
      });
    }
    if (isAppointmentPurpose) {
      const prefix = APPOINTMENT_PURPOSE_PREFIXES.find((entry) =>
        data.purpose.startsWith(entry),
      );
      const purposeAppointmentId = prefix
        ? data.purpose.slice(prefix.length).trim()
        : "";
      if (!purposeAppointmentId && !data.appointmentId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["purpose"],
          message: "appointment verification must include an appointment id",
        });
      }
      if (
        data.appointmentId &&
        purposeAppointmentId &&
        data.appointmentId !== purposeAppointmentId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["appointmentId"],
          message: "appointmentId must match the id encoded in purpose",
        });
      }
    }
  });

export const sendVerificationResponseSchema = z.object({
  sent: z.boolean(),
  method: z.enum(["sms", "email"]).optional(),
});

export const verifyVerificationRequestSchema = z
  .object({
    phoneNumber: phoneNumberSchema.optional(),
    email: z.string().email().optional(),
    purpose: purposeSchema,
    code: verificationCodeSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.phoneNumber && !data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["phoneNumber"],
        message: "phoneNumber or email is required",
      });
    }
  });

export const verifyVerificationResponseSchema = z.object({
  verified: z.boolean(),
});

export const cancelVerificationRequestSchema = z.object({
  phoneNumber: phoneNumberSchema.optional(),
  email: z.string().email().optional(),
  purpose: purposeSchema,
});

export const cancelVerificationResponseSchema = z.object({
  canceled: z.boolean(),
});

export type SendVerificationRequest = z.infer<
  typeof sendVerificationRequestSchema
>;
export type SendVerificationResponse = z.infer<
  typeof sendVerificationResponseSchema
>;
export type VerifyVerificationRequest = z.infer<
  typeof verifyVerificationRequestSchema
>;
export type VerifyVerificationResponse = z.infer<
  typeof verifyVerificationResponseSchema
>;
export type CancelVerificationRequest = z.infer<
  typeof cancelVerificationRequestSchema
>;
export type CancelVerificationResponse = z.infer<
  typeof cancelVerificationResponseSchema
>;
